// Theme management — light / dark / system.
//
// Two distinct concepts, and keeping them apart is the whole design:
//
//   PREFERENCE  what the user chose. Persisted. 'light' | 'dark' | 'system'.
//   RESOLVED    what is actually applied to <html>. Never 'system' — always
//               'light' or 'dark'.
//
// 'system' follows the OS via matchMedia and updates LIVE, which is why the
// toggle no longer reloads the page. A reload was also the only way the old
// implementation could have noticed an OS change, which is a poor trade for
// what is ultimately a class swap.

const KEY = 'ug_theme';
const VALID = ['light', 'dark', 'system'];

// New users get 'system'. Anyone with a stored preference is untouched — this
// default applies only when the key is absent or holds an unrecognised value.
const DEFAULT = 'system';

const mq = () =>
  (typeof window !== 'undefined' && window.matchMedia)
    ? window.matchMedia('(prefers-color-scheme: dark)')
    : null;

/** The stored PREFERENCE. May be 'system'. */
export function getTheme() {
  try {
    const v = localStorage.getItem(KEY);
    return VALID.includes(v) ? v : DEFAULT;
  } catch {
    return DEFAULT;
  }
}

/** The theme to actually apply. Never returns 'system'. */
export function resolveTheme(pref = getTheme()) {
  if (pref !== 'system') return pref;
  const m = mq();
  return m && m.matches ? 'dark' : 'light';
}

/**
 * Apply a resolved theme to <html> WITHOUT persisting it.
 *
 * useForceDark needs this. The previous implementation called setTheme('dark'),
 * which WROTE 'dark' to localStorage — so opening /login, /terms or /privacy
 * silently overwrote the user's saved preference, and refreshing while on one
 * of those pages made it permanent. Applying without persisting is the fix.
 */
export function applyTheme(resolved) {
  const html = document.documentElement;
  html.classList.remove('dark', 'light');
  html.classList.add(resolved);
}

// While a page forces a theme (see useForceDark), an OS change must not yank it
// back. The media listener checks this and stands down.
let forced = null;
export function setForcedTheme(resolved) { forced = resolved; }

/** Set the user's PREFERENCE. Persists, then applies the resolved value. */
export function setTheme(pref) {
  const next = VALID.includes(pref) ? pref : DEFAULT;
  try { localStorage.setItem(KEY, next); } catch {}
  if (!forced) applyTheme(resolveTheme(next));
  return next;
}

/**
 * Boot. Applies the current preference and subscribes to OS changes so that
 * 'system' tracks live — no reload required.
 */
export function initTheme() {
  applyTheme(resolveTheme());
  const m = mq();
  if (!m) return;
  const onChange = () => {
    // Only 'system' follows the OS. An explicit light/dark choice ignores it,
    // and a forced page keeps its forced value until it unmounts.
    if (forced) return;
    if (getTheme() === 'system') applyTheme(resolveTheme());
  };
  if (m.addEventListener) m.addEventListener('change', onChange);
  else if (m.addListener) m.addListener(onChange);   // Safari < 14
}
